/**
 * ImageEditorDialog – shared image editor with crop and rotation.
 *
 * Used for:
 * 1. New cover upload (crop to 16:9)
 * 2. Gallery → cover promotion (crop to 16:9)
 * 3. Re-editing existing cover or gallery images
 *
 * Returns an edited File via onSave callback.
 *
 * Shows resolution feedback and offers gallery fallback when the image is
 * too small for cover usage.
 */

import { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { RotateCw, CheckCircle2, AlertTriangle, ImageIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { exportEditedFile, type CropArea } from '@/lib/media/imageEditor';
import { assessImageSuitability, type SuitabilityResult } from '@/lib/media/assessImageSuitability';
import { type ImageIntent, getIntentThresholds } from '@/lib/media/imageIntent';
import { toast } from 'sonner';

const COVER_ASPECT = 16 / 9;

export type EditorMode = 'cover' | 'gallery';

interface ImageEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Object URL or public URL of the image to edit */
  imageSrc: string;
  /** Original file name – used to derive output file name */
  originalFileName: string;
  /** Called with the edited File when user saves */
  onSave: (file: File) => void;
  /** Called when user chooses gallery fallback (caller uses original file) */
  onSaveAsGallery?: () => void;
  /** Loading state while parent is uploading the result */
  isSaving?: boolean;
  /** Image intent for context-aware quality rules. Required — no silent hero default. */
  intent: ImageIntent;
  /** Editor mode: 'cover' forces 16:9 aspect, 'gallery' uses free crop. Defaults to 'cover'. */
  mode?: EditorMode;
}

export function ImageEditorDialog({
  open,
  onOpenChange,
  imageSrc,
  originalFileName,
  onSave,
  onSaveAsGallery,
  isSaving = false,
  intent,
  mode = 'cover',
}: ImageEditorDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Source image natural dimensions
  const [srcWidth, setSrcWidth] = useState(0);
  const [srcHeight, setSrcHeight] = useState(0);
  // Cropped output dimensions (updated on each crop change)
  const [cropWidth, setCropWidth] = useState(0);
  const [cropHeight, setCropHeight] = useState(0);
  const [suitability, setSuitability] = useState<SuitabilityResult>({ suitability: 'ok' });

  const isCoverMode = mode === 'cover';
  const aspect = isCoverMode ? COVER_ASPECT : undefined;

  // Reset state when dialog opens with new image
  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setCropWidth(0);
    setCropHeight(0);
  }, [open, imageSrc]);

  // Measure source image when it changes
  useEffect(() => {
    if (!imageSrc || !open) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setSrcWidth(img.naturalWidth);
      setSrcHeight(img.naturalHeight);
      // Initial suitability based on source dimensions (will update on crop)
      setSuitability(assessImageSuitability(img.naturalWidth, img.naturalHeight, intent));
      console.debug('[ImageEditor] source loaded', {
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        mode,
      });
    };
    img.src = imageSrc;
  }, [imageSrc, open]);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
    setCropWidth(Math.round(croppedPixels.width));
    setCropHeight(Math.round(croppedPixels.height));
    // Re-assess suitability based on cropped output
    const result = assessImageSuitability(
      Math.round(croppedPixels.width),
      Math.round(croppedPixels.height),
      intent,
    );
    setSuitability(result);
  }, []);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const buildEditedFile = useCallback(async () => {
    if (!croppedAreaPixels) return null;
    const cropArea: CropArea = {
      x: Math.round(croppedAreaPixels.x),
      y: Math.round(croppedAreaPixels.y),
      width: Math.round(croppedAreaPixels.width),
      height: Math.round(croppedAreaPixels.height),
    };
    return exportEditedFile(imageSrc, cropArea, rotation, originalFileName);
  }, [croppedAreaPixels, imageSrc, rotation, originalFileName]);

  const handleSave = useCallback(async () => {
    try {
      const editedFile = await buildEditedFile();
      if (!editedFile) return;

      // Quality check on the cropped output
      const img = new Image();
      const url = URL.createObjectURL(editedFile);
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject();
        img.src = url;
      });
      URL.revokeObjectURL(url);

      const result = assessImageSuitability(img.naturalWidth, img.naturalHeight, intent);

      if (result.suitability === 'block') {
        toast.error(
          result.message ??
            'Den beskurna bilden blev för liten för att fungera bra.',
        );
        return;
      }

      if (result.suitability === 'warn') {
        toast.warning(
          result.message ??
            'Bilden kan användas men högre upplösning rekommenderas.',
        );
      }

      onSave(editedFile);
    } catch (err) {
      console.error('[ImageEditor] export failed', err);
      toast.error('Kunde inte spara den redigerade bilden.');
    }
  }, [buildEditedFile, onSave]);

  const handleSaveAsGallery = useCallback(() => {
    if (!onSaveAsGallery) return;
    onSaveAsGallery();
  }, [onSaveAsGallery]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setSrcWidth(0);
    setSrcHeight(0);
    setCropWidth(0);
    setCropHeight(0);
  }, [onOpenChange]);

  const isBlocked = suitability.suitability === 'block';

  const titleText = isCoverMode ? 'Justera omslagsbild' : 'Redigera bild';
  const descriptionText = isCoverMode
    ? 'Flytta och zooma bilden så att den fungerar bra som omslagsbild.'
    : 'Beskär, rotera och justera bilden.';
  const saveLabel = isCoverMode ? 'Spara omslagsbild' : 'Spara bild';
  const thresholds = getIntentThresholds(intent);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle>{titleText}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {descriptionText}
          </DialogDescription>
        </DialogHeader>

        {/* Crop area */}
        <div className="relative w-full bg-black" style={{ height: '340px' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* Resolution info + suitability feedback */}
        <div className="px-4 pt-3 space-y-1.5">
          {srcWidth > 0 && (
            <p className="text-sm text-muted-foreground">
              Originalbild: {srcWidth} × {srcHeight} px
            </p>
          )}
          {cropWidth > 0 && (
            <p className="text-sm text-muted-foreground">
              Beskuren bild: {cropWidth} × {cropHeight} px
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Rekommenderat för {thresholds.contextLabel}: minst {thresholds.recommendedWidth} px bred
          </p>

          {suitability.suitability === 'ok' && cropWidth > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-emerald-600">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>Bra storlek för {thresholds.contextLabel}</span>
            </div>
          )}
          {suitability.suitability === 'warn' && cropWidth > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-amber-600">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Bilden fungerar som {thresholds.contextLabel} men högre upplösning ger bättre resultat.</span>
            </div>
          )}
          {suitability.suitability === 'block' && cropWidth > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-amber-600">
              <ImageIcon className="h-4 w-4 shrink-0" />
              <span>{isCoverMode ? 'Den här bilden fungerar bättre i galleriet.' : 'Bilden är för liten för att ge bra resultat.'}</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="px-4 py-3 space-y-3">
          {/* Zoom */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground w-10 shrink-0">Zoom</span>
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.05}
              onValueChange={([v]) => setZoom(v)}
              className="flex-1"
            />
          </div>

          {/* Rotation info */}
          {rotation > 0 && (
            <p className="text-xs text-muted-foreground">
              Roterad {rotation}°
            </p>
          )}
        </div>

        <DialogFooter className="px-4 pb-4 pt-0 flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRotate}
            disabled={isSaving}
          >
            <RotateCw className="h-4 w-4 mr-1.5" />
            Rotera
          </Button>
          <div className="flex-1" />

          {isBlocked && onSaveAsGallery && isCoverMode ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Välj annan bild
              </Button>
              <Button
                type="button"
                size="sm"
                data-testid="event-media-editor-save-as-gallery"
                onClick={handleSaveAsGallery}
                disabled={isSaving || !croppedAreaPixels}
              >
                {isSaving ? 'Sparar…' : 'Lägg i galleri'}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="event-media-editor-cancel"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Avbryt
              </Button>
              <Button
                type="button"
                size="sm"
                data-testid="event-media-editor-save-cover"
                onClick={handleSave}
                disabled={isSaving || !croppedAreaPixels}
              >
                {isSaving ? 'Sparar…' : saveLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
